import { CircularProgress } from '@civilgrid/ui/components';
import { Link } from '@tanstack/react-router';
import classNames from 'classnames';
import type React from 'react';
import { memo, type ReactNode, useMemo } from 'react';
import { planStatusLabels } from '~/common/projectUtils';
import { type IntelligenceSummary, PlanStatus } from '~/common/types';
import HoverCard from '~/components2/HoverCard';
import InfoTooltip from '~/components2/InfoTooltip';
import type { ExportableColumnDef } from '~/components2/Table';
import { columns as baseColumns, constructionEndDateColumn } from './columns';
import { pgeGasInformation, pgeRegionAndDivision, sceColumns } from './customColumns';

type Def = ExportableColumnDef<IntelligenceSummary>;

export const renderHeaderWithTooltip = (title: string, tooltip: ReactNode) => {
  return (
    <div className="flex items-center space-x-1">
      <div>{title}</div>
      <InfoTooltip text={tooltip} label={`About ${title}`} />
    </div>
  );
};

export const pgeCell = (value: string) => {
  return <span className="capitalize">{value.toLowerCase()}</span>;
};

export const YesNoCell = ({ value }: { value: string }) => {
  return value;
};

export function isMissingDates(summary: IntelligenceSummary): boolean {
  return !summary.project.constructionStartDate || !summary.project.constructionEndDate;
}

// A row has prior compute data if any field that's only populated by a successful
// computeIntelligence run is non-null. The job's error path doesn't clear these
// fields, so when computeStatus === 'error' and these are set, the row is showing
// the last successful result.
export function hasPriorComputeData(summary: IntelligenceSummary): boolean {
  return summary.rightOfWayArea != null || summary.conflictArea != null || !!summary.schedule?.recommendation;
}

export const StaleCell = ({ value, 'data-testid': dataTestId }: { value: ReactNode; 'data-testid'?: string }) => {
  return (
    <HoverCard
      trigger={
        <span className={classNames('inline cursor-pointer rounded-sm px-1')} data-testid={dataTestId}>
          ⚠️ {value}
        </span>
      }
    >
      <div className="flex flex-col text-sm">
        <span className="font-semibold">⚠️ Stale data</span>
        <span>This summary failed to refresh and may not reflect recent changes. We're looking into it.</span>
      </div>
    </HoverCard>
  );
};

export const EmptyCell = () => {
  return (
    <HoverCard trigger={<span className={classNames('inline cursor-pointer px-1 italic text-grey-50')}>—</span>}>
      <div className="flex flex-col text-sm">
        <span className="font-semibold">⚠️ No data available</span>
        <span>The compute intelligence job failed and no prior data is available.</span>
      </div>
    </HoverCard>
  );
};

export const getSummaryValue = ({
  summary,
  defaultValue = 0,
  value,
}: {
  summary: IntelligenceSummary;
  defaultValue?: any;
  value: any;
}): any => {
  if (isMissingDates(summary)) {
    return defaultValue;
  }

  return value;
};

export const toMiles = (num: number): number => {
  return Number((num / 5280).toFixed(2));
};

export const MissingInfo = ({
  projectId,
  'data-testid': dataTestId,
}: {
  projectId: string;
  'data-testid'?: string;
}) => {
  return (
    <HoverCard
      trigger={
        <span className={classNames('inline rounded-sm px-1 text-red-500 cursor-pointer')} data-testid={dataTestId}>
          ⚠️ Missing Info
        </span>
      }
    >
      <div className="flex flex-col text-sm">
        <span className="font-semibold">⚠️ Missing Info:</span>
        <span>
          Construction start date and end date is required to compute intelligence.{' '}
          <Link
            to="/projects/$projectId/$tabId"
            params={{ projectId, tabId: 'details' }}
            className="text-blue-50 underline hover:text-blue-60"
          >
            Update Project Info
          </Link>
          .
        </span>
      </div>
    </HoverCard>
  );
};

export const Cell = memo(
  ({
    value,
    warning,
    summary,
    tooltip,
    'data-testid': dataTestId,
  }: {
    value: number | string;
    warning?: boolean;
    summary: IntelligenceSummary;
    tooltip?: React.ReactNode;
    'data-testid'?: string;
  }) => {
    if (isMissingDates(summary)) {
      return <MissingInfo projectId={summary.project.id} data-testid={dataTestId} />;
    }

    if (summary.computeStatus === 'processing') {
      return (
        <span data-testid={dataTestId}>
          <CircularProgress indeterminate />
        </span>
      );
    }

    if (summary.computeStatus === 'error') {
      if (!hasPriorComputeData(summary)) {
        return <EmptyCell />;
      }
      return <StaleCell value={value === '' || value == null ? '—' : value} data-testid={dataTestId} />;
    }

    if (warning) {
      return (
        <HoverCard
          trigger={
            <span
              className={classNames('inline rounded-sm bg-yellow-100 px-1 text-yellow-900')}
              data-testid={dataTestId}
            >
              {value}
            </span>
          }
        >
          <div className="flex flex-col">
            <span className="font-semibold">⚠️ NOTE:</span>
            <span>This project contains non-digitized data for some agencies.</span>
          </div>
        </HoverCard>
      );
    }

    const content = (
      <div className="px-1" data-testid={dataTestId}>
        {value}
      </div>
    );

    if (tooltip) {
      return <HoverCard trigger={content}>{tooltip}</HoverCard>;
    }

    return content;
  },
);

interface SummaryConfig {
  showCharts: boolean;
  showGasInfo: boolean;
  columns: typeof baseColumns;
  statusLabels: { [key in PlanStatus]: string };
}

const PGE_PLAN_STATUS_LABELS = {
  [PlanStatus.Pending]: 'Project Management Review',
  [PlanStatus.NeedsAttention]: 'TBD',
  [PlanStatus.Scheduled]: 'Cleared',
};

export const useSummaryConfig = ({ conflictsSummaryConfig }: { conflictsSummaryConfig?: string[] }) => {
  return useMemo(() => {
    const config: SummaryConfig = {
      showCharts: false,
      showGasInfo: false,
      columns: [...baseColumns],
      statusLabels: planStatusLabels,
    };

    if (conflictsSummaryConfig?.includes('pge')) {
      config.showCharts = true;
      config.showGasInfo = true;
      config.columns.splice(2, 0, ...pgeRegionAndDivision);
      config.columns.splice(11, 0, ...pgeGasInformation);
      config.statusLabels = PGE_PLAN_STATUS_LABELS;
    }

    if (conflictsSummaryConfig?.includes('sce')) {
      config.columns.push(...sceColumns);
    }

    const constructionStartDateIndex = config.columns.findIndex((c) => c.id === 'constructionStartDate');
    if (constructionStartDateIndex !== -1) {
      config.columns.splice(constructionStartDateIndex + 1, 0, constructionEndDateColumn);
    }

    return config;
  }, [conflictsSummaryConfig]);
};
